/* eslint-disable react/display-name */
/* eslint-disable react/prop-types */
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMemo } from 'react';
import { Checkbox, Input, Button, Form } from 'semantic-ui-react';
import { Addons } from '../../api/services/next';
import { GetProducts } from "../../api/services/pms";
import { useCurrentHotel } from '../../hooks/useCurrentHotel';
import { useMessageResources, useTextResources } from "../../language";
import GridPanel from '../GridPanel';
import { dateFormat, timeFormat } from '../shared/constants';
import { Currencies } from "../shared/currencies";
import { DatePicker } from "../shared/DatePicker";
import { HotelAddonDropDown } from '../shared/HotelAddonDropDown';
import { onError, onWarning } from "../shared/messages";
import NumericInput from "../shared/NumericInput";
import { groupByIntoArray, roundToPrecision } from '../shared/utils';
import { useLoader } from '../../hooks/useLoader';
import "./style.less";

const ExtraAddonsGrid = ({
    reservation,
    data,
    insufficientAddon,
    setInsufficientAddon,
    ...rest }) => {
    const text = useTextResources()
    const messages = useMessageResources()
    const loader = useLoader();

    const isProductPricingIncludesTax = useCurrentHotel()?.HotelParameter?.IsProductPricingIncludesTax
    const [products, setProducts] = useState([])
    const [selected, setSelected] = useState([]);
    const [hotelAddonData, setHotelAddonData] = useState([])
    const [isDataLoaded, setIsDataLoaded] = useState(false)
    const pendingRowsRef = useRef([])

    useEffect(() => {
        (async () => {
            if (!rest.hotelId && !rest.startDate && !rest.endDate) {
                return;
            }
            try {
                loader(true);
                const [product, hotelAddon] = await Promise.all([
                    GetProducts({
                        HotelID: rest.hotelId,
                        StartDate: moment(rest.startDate).toISOString(),
                        EndDate: moment(rest.endDate).add(1, 'day').toISOString(),
                        MaxAdults: reservation?.Adults || 0,
                        MaxChildren: reservation?.Children || 0,
                        MaxInfants: reservation?.Infants || 0,
                    }),
                    Addons.GetExtraAddons({
                        HotelId: rest.hotelId,
                        FromDate: moment(rest.startDate).toISOString(),
                        ToDate: moment(rest.endDate).add(1, 'day').toISOString(),
                        RateId: rest.rateId,
                        RoomTypeIds: [reservation.RoomTypeId]
                    }),
                ])

                setProducts(product)
                setHotelAddonData(hotelAddon)
            } catch (err) {
                onError(err)
            } finally {
                loader(false);
                setIsDataLoaded(true);
            }
        })()
    }, [rest.hotelId, rest.startDate, rest.endDate, rest.rateId]);

    // Collect rows that need applyAddonPrices once data is loaded
    useEffect(() => {
        if (!isDataLoaded || !hotelAddonData.length || !data?.length) return;

        const rowsWithAddon = data.filter(row => row.HotelAddonId && !row.FolioTransactionID && !row.FolioTransactionId);
        if (rowsWithAddon.length > 0) {
            pendingRowsRef.current = rowsWithAddon.map(row => ({ id: row.HotelAddonId, row }));
        }
    }, [isDataLoaded, hotelAddonData.length]);

    const gridData = useMemo(() => {
        return data ?? [];
    }, [data])

    const setData = (data) => {
        rest.setData?.(data);
    }

    const setDataForDeletion = (data) => {
        rest.setDataForDeletion?.(data);
    }

    const manualFormatter = useCallback(({ row }) => {
        if (row.RoomAmount === null && (
            row.ChildAmount != row.OriginalChildAmount ||
            row.AdultAmount != row.OriginalAdultAmount ||
            row.InfantAmount != row.OriginalInfantAmount ||
            row.ChildQuantity != reservation.Children ||
            row.AdultQuantity != reservation.Adults ||
            row.InfantQuantity != reservation.Infants) ||
            (row.RoomAmount != row.OriginalRoomAmount ||
                row.Quantity != 1)
        ) {
            return <Checkbox checked={true} />
        }
        return <Checkbox checked={false} />

    }, [reservation])

    const currencyFormatter = useCallback(({ value }) => {
        return Currencies[value]?.symbol || ''
    }, [])
    const dateFormatter = useCallback(({ value }) => {
        return value && moment(value).format(dateFormat) || ''
    }, [])
    const timeFormatter = useCallback(({ value }) => {
        return value && moment(value).format(timeFormat) || ""
    }, [])
    const productFormatter = useCallback(({ value }) => {
        return products && products.find(r => r.Id === value)?.ProductName || '';
    }, [products])
    const totalAmountFormatter = useCallback(({ row }) => {
        if (row.RoomAmount === null || row.RoomAmount === 0) {
            return ((row.AdultQuantity || 0) * (row.AdultAmount || 0) +
                (row.ChildQuantity || 0) * (row.ChildAmount || 0) +
                (row.InfantQuantity || 0) * (row.InfantAmount || 0)).toFixed(2)
        }
        else {
            var roomAmountQuinty = !row.Quantity ? 1 : row.Quantity;
            return ((roomAmountQuinty || 0) * (row.RoomAmount || 0)).toFixed(2)
        }
    }, [])

    const addNewDailyAddon = useCallback(() => {
        setData([...gridData,
        {
            Id: `New-${crypto.randomUUID()}`,
            Date: reservation.CheckInDate,
            ProductID: null,
            FolioTransactionID: null
        }])
    }, [gridData, reservation])

    const deleteAddon = useCallback(() => {
        let updated = [...gridData]
        if (selected.length) {
            const selectedIdsForDeletion = selected.filter((index) => !isInputDisabled(updated[index]))
                .map((index) => updated[index].Id);

            const deletedRowsForSave = updated
                .filter((item) => selectedIdsForDeletion.includes(item.Id) &&
                    typeof item.Id != 'string')
                .map((item) => ({ ...item, IsDeleted: true }));

            updated.filter((item) => selectedIdsForDeletion.includes(item.Id)).map(row => {
                updateAddonAvailabilty({
                    ...row, Quantity: 0,
                    AdultQuantity: 0,
                    ChildQuantity: 0,
                    InfantQuantity: 0
                }, row)
            })

            updated = updated.filter(
                (item) => !selectedIdsForDeletion.includes(item.Id));

            setSelected([])
            setDataForDeletion(deletedRowsForSave)

        }
        setData(updated)

    }, [selected, gridData])

    const validation = useCallback((row, property, val, oldRow) => {

        if (!row.HotelAddonId) {
            onWarning(messages('ChainAddonIsMissing'));
            return false;
        }

        const currentAddon = hotelAddonData.find(x => x.Id == row.HotelAddonId && moment(x.Date).isSame(row.Date, "date"))

        //Check availability
        if (!currentAddon?.IsAddonAvailable) {
            onWarning(messages('ReservationAddonsNotInRangeOfReservationDates'));
            return false;
        }
        //Check quantities
        const quantity = parseInt(row.Quantity??0) +
            Number(row.AdultQuantity??0) +
            Number(row.ChildQuantity??0) +
            Number(row.InfantQuantity??0);

        // var prevRow = originalData.find(x => x.Id === row.Id)
        const prevQuantity = moment(oldRow.Date).isSame(row.Date, "date") ? 
            Number(oldRow?.Quantity??0) +
            Number(oldRow?.AdultQuantity??0) +
            Number(oldRow?.ChildQuantity??0) +
            Number(oldRow?.InfantQuantity??0) : 0

        // Allow setting individual quantities to 0, but warn if total becomes 0
        // The save validation will catch rows with zero total quantity
        if (quantity === 0) {
            onWarning(messages('QuantityIsRequired'));
        }

        if (currentAddon?.AddonAvailability < quantity - (prevQuantity || 0)) {
            onWarning("Addon has insufficient quantity")
            setInsufficientAddon(true);
            return 'insufficient'; // Return special value to allow data update but skip availability update
        }
        setInsufficientAddon(false);

        //Check max pax and max night
        if (currentAddon?.MaxNights &&
            property === "Date" &&
            !chackMaxNight(row, currentAddon)) {
            onWarning(messages('MaxNight').replace('{0}', currentAddon.MaxNights))
            return false
        }

        if (currentAddon?.MaxPax) {
            const totalPaxWithoutCurrent = totalPaxPerAddon(currentAddon.Id, row.Date, row.Id)
            const totalCurrentPax = row.AdultQuantity + row.ChildQuantity + row.InfantQuantity

            if (totalPaxWithoutCurrent + totalCurrentPax > currentAddon.MaxPax) {
                val = property === "Date" ? val : row[property] - (totalPaxWithoutCurrent + totalCurrentPax - currentAddon.MaxPax)
                const updated = { ...row, [property]: val }
                setData(gridData.map((x) => (x.Id === row.Id ? updated : x)));
                onWarning(messages('MaxPax').replace('{0}', currentAddon.MaxPax))
                return false
            }
        }

        return true
    }, [hotelAddonData, gridData])

    const calculatePax = (row) => {
        return (row?.Quantity || 0) +
            (row?.AdultQuantity || 0) +
            (row?.ChildQuantity || 0) +
            (row?.InfantQuantity || 0)
    }

    const isSameAddonAndDate = (addon, row) =>
        addon.Id === row.HotelAddonId && moment(addon.Date).isSame(row.Date, "date");

    const updateAddonAvailabilty = (newRow, oldRow) => {
        const oldPax = calculatePax(oldRow);
        const newPax = calculatePax(newRow);

        setHotelAddonData(hotelAddonData.map(addon => {

            const isOld = isSameAddonAndDate(addon, oldRow);
            const isNew = isSameAddonAndDate(addon, newRow);

            let AddonAvailability = Number(addon.AddonAvailability);

            //Check if addon or date was changed
            if (isOld && !isNew) {
                AddonAvailability += oldPax
            }
            if (!isOld && isNew) {
                AddonAvailability -= newPax
            }
            if (isOld && isNew) {
                AddonAvailability -= (newPax - oldPax)
            }

            return {
                ...addon,
                AddonAvailability,
                IsAddonAvailable: AddonAvailability > 0
            };
        }
        ))
    }

    const changeRow = useCallback((property, id, newValue) => {
        var item = gridData.find(x => x.Id === id);

        // clean up HotelAddonId if not available
        if (property === "Date") {
            if (!hotelAddonData
                .filter(x => moment(x.Date).isSame(newValue, "date") && x.IsAddonAvailable)
                .some(x => x.Id === item.HotelAddonId)) {
                item.HotelAddonId = null;
            }
        }

        const validateProperty = { ...item, [property]: newValue }

        // just allow change date without validation when addon was not selected yet
        const isDateChangedWithoutSelectedAddon = !item.HotelAddonId && property === "Date";
        const validationResult = isDateChangedWithoutSelectedAddon ? true : validation(validateProperty, property, newValue, item);
        
        // Block update only for hard validation failures (false), allow for 'insufficient' to preserve user value
        if (validationResult === false)
            return;
        
        setData(gridData.map((item) =>
            item.Id !== id
                ? item
                : {
                    ...item,
                    [property]:
                        typeof newValue == 'number' ? parseInt(newValue) : newValue,
                    //Update prices
                    RoomAmountBeforeTax: property === 'RoomAmount' ? newValue : item.RoomAmountBeforeTax,
                    RoomAmountAfterTax: property === 'RoomAmount' ? newValue : item.RoomAmountAfterTax,
                    AdultAmountBeforeTax: property === 'AdultAmount' ? newValue : item.AdultAmountBeforeTax,
                    AdultAmountAfterTax: property === 'AdultAmount' ? newValue : item.AdultAmountAfterTax,
                    ChildAmountBeforeTax: property === 'ChildAmount' ? newValue : item.ChildAmountBeforeTax,
                    ChildAmountAfterTax: property === 'ChildAmount' ? newValue : item.ChildAmountAfterTax,
                    InfantAmountBeforeTax: property === 'InfantAmount' ? newValue : item.InfantAmountBeforeTax,
                    InfantAmountAfterTax: property === 'InfantAmount' ? newValue : item.InfantAmountAfterTax,
                    //Update ConsumptionDate
                    ConsumptionDate: property === 'Date' ? newValue : item.ConsumptionDate
                }
        ));
        
        // Only update availability when validation fully passes (not for insufficient quantity)
        if (validationResult === true) {
            updateAddonAvailabilty(validateProperty, item)
        }

    }, [gridData, hotelAddonData]);

    const createEditableFormatter = useCallback((property) => {

        /* eslint-disable-next-line react/display-name, react/prop-types*/
        const digits = property.includes("Amount") ? 2 : 0
        return ({ value, row }) => (
            <Form.Input
                control={NumericInput}
                disabled={isInputDisabled(row) || (value != 0 && !value)}
                value={value}
                style={{ width: '100%' }}
                buttonPlacement='none'
                min={0}
                digits={digits}
                onBlur={(event) => {
                    return changeRow(property, row.Id, roundToPrecision(event.target.value, digits));
                }}
            />
        )
    }, [gridData, hotelAddonData]);

    const createDateFormatter = useCallback(({ value, row }) => (
        <div className='extra-addon-date overflow-visible' style={{ width: '100%' }}>
            <DatePicker
                disabled={isInputDisabled(row)}
                value={moment(value || reservation.CheckInDate).toDate()}
                className='extra-addon-date'
                onChange={(value) => changeRow("Date", row.Id, value || reservation.CheckInDate)}
                minDate={moment(rest.startDate).startOf('day').toDate()}
                maxDate={moment(rest.endDate).endOf('day').toDate()} />
        </div>
    ), [reservation, rest.startDate, rest.endDate, gridData]);

    const applyAddonPrices = useCallback((value, row) => {
        const currentAddon = hotelAddonData.find(x => x.Id == value && moment(x.Date).isSame(row.Date, "date")) || hotelAddonData.find(x=>x?.ChainAddonId == row?.HotelAddon?.ChainAddonID && moment(x.Date).isSame(row.Date, "date"));

        if (!currentAddon)
            return

        let totalPax = Infinity

        if (currentAddon?.MaxNights && !chackMaxNight(row, currentAddon)) {
            onWarning(messages('MaxNight').replace('{0}', currentAddon.MaxNights))
            return
        }

        if (currentAddon?.MaxPax) {
            totalPax = currentAddon.MaxPax - totalPaxPerAddon(currentAddon.Id, row.Date, row.Id)
            if (totalPax === 0) {
                onWarning(messages('MaxPax').replace('{0}', currentAddon.MaxPax))
                return
            }
        }

        if (!currentAddon?.IsAddonAvailable) {
            onWarning("Addon is not available for sale")
            return;
        } else if (reservation.Quantity > 0) {
            if (currentAddon?.AddonAvailability < reservation.Quantity) {
                onWarning("Addon has insuffecient quantity")
                return;
            }
        }


        const adultQuantity = Math.min(reservation.Adults, Math.min(totalPax, currentAddon.AddonAvailability));
        totalPax -= adultQuantity;

        const childQuantity = Math.min(reservation.Children, Math.min(totalPax, currentAddon.AddonAvailability));
        totalPax -= childQuantity;

        const infantQuantit = Math.min(reservation.Infants, Math.min(totalPax, currentAddon.AddonAvailability));

        let updatedRow = {}

        const isPerRoom = currentAddon.RatePricingElement?.RatePricingTypeId === 1;
        const isPerPerson = currentAddon.RatePricingElement?.RatePricingTypeId === 2;
        const normalizedAddonId = Number(value);
        const isSameAddon = Number(row.HotelAddonId) === normalizedAddonId;

        setData(gridData.map((item) => {
            if (item.Id === row.Id) {
                item = {
                    ...item,
                    ChildAmount: isPerPerson ?
                        (isProductPricingIncludesTax ? currentAddon.ChildAmountAfterTax : currentAddon.ChildAmountBeforeTax) : null,
                    ChildAmountBeforeTax: isPerPerson ? currentAddon.ChildAmountBeforeTax : null,
                    ChildAmountAfterTax: isPerPerson ? currentAddon.ChildAmountAfterTax : null,
                    OriginalChildAmount: isPerPerson ?
                        (isProductPricingIncludesTax ? currentAddon.ChildAmountAfterTax : currentAddon.ChildAmountBeforeTax) : null,

                    InfantAmount: isPerPerson ?
                        (isProductPricingIncludesTax ? currentAddon.InfantAmountAfterTax : currentAddon.InfantAmountBeforeTax) : null,
                    InfantAmountBeforeTax: isPerPerson ? currentAddon.InfantAmountBeforeTax : null,
                    InfantAmountAfterTax: isPerPerson ? currentAddon.InfantAmountAfterTax : null,
                    OriginalInfantAmount: isPerPerson ?
                        (isProductPricingIncludesTax ? currentAddon.InfantAmountAfterTax : currentAddon.InfantAmountBeforeTax) : null,

                    AdultAmount: isPerPerson ?
                        (isProductPricingIncludesTax ? currentAddon.AdultAmountAfterTax : currentAddon.AdultAmountBeforeTax) : null,
                    AdultAmountBeforeTax: isPerPerson ? currentAddon.AdultAmountBeforeTax : null,
                    AdultAmountAfterTax: isPerPerson ? currentAddon.AdultAmountAfterTax : null,
                    OriginalAdultAmount: isPerPerson ?
                        (isProductPricingIncludesTax ? currentAddon.AdultAmountAfterTax : currentAddon.AdultAmountBeforeTax) : null,

                    RoomAmount: isPerRoom ?
                        (isProductPricingIncludesTax ? currentAddon.RoomAmountAfterTax : currentAddon.RoomAmountBeforeTax) : null,
                    RoomAmountBeforeTax: isPerRoom ? currentAddon.RoomAmountBeforeTax : null,
                    RoomAmountAfterTax: isPerRoom ? currentAddon.RoomAmountAfterTax : null,
                    OriginalRoomAmount: isPerRoom ?
                        (isProductPricingIncludesTax ? currentAddon.RoomAmountAfterTax : currentAddon.RoomAmountBeforeTax) : null,

                    AdultQuantity: isPerPerson ? (isSameAddon && item.AdultQuantity != null ? item.AdultQuantity : adultQuantity) : null,
                    ChildQuantity: isPerPerson ? (isSameAddon && item.ChildQuantity != null ? item.ChildQuantity : childQuantity) : null,
                    InfantQuantity: isPerPerson ? (isSameAddon && item.InfantQuantity != null ? item.InfantQuantity : infantQuantit) : null,
                    Quantity: isPerRoom ? (isSameAddon && item.Quantity != null ? item.Quantity : 1) : null,

                    HotelAddonId: value,
                    CurrencyId: currentAddon.CurrencyId,
                    IsActive: true,
                    ConsumptionDate: row.Date
                }
                updatedRow = item
            }
            return item
        }
        ));
        updateAddonAvailabilty(updatedRow, row)
    }, [hotelAddonData, gridData, reservation])

    // Process pending rows one at a time by calling applyAddonPrices
    useEffect(() => {
        if (pendingRowsRef.current.length === 0) return;

        const { id, row } = pendingRowsRef.current.shift();
        const currentRow = gridData.find(r => r.Id === row.Id);
        if (currentRow) {
            applyAddonPrices(id, currentRow);
        }
    }, [gridData, applyAddonPrices]);

    const chackMaxNight = useCallback((row, currentAddon) => {
        const filteredData = gridData.filter(x => x.HotelAddonId === currentAddon.Id && x.Id != row.Id)

        const dateAlreadyExists = filteredData.some(x => moment(x.Date).isSame(row.Date, "date"));
        if (dateAlreadyExists)
            return true

        const nightCount = groupByIntoArray(filteredData, "Date")?.length || 0;

        if (nightCount === currentAddon.MaxNights)
            return false;

        return true;
    }, [gridData])

    const totalPaxPerAddon = useCallback((addonId, date, rowId) => {
        return gridData
            .filter(x => x.Id != rowId && x.HotelAddonId === addonId && moment(x.Date).isSame(date, "date"))
            .reduce((acc, obj) => acc + obj.AdultQuantity + obj.ChildQuantity + obj.InfantQuantity, 0);
    }, [gridData]);

    const createAddonFormatter = useCallback(({ value, row }) => {
        // Get addon Ids already in the grid (except for the current row)   
        return (
            isInputDisabled(row) ?
                <Input
                    disabled={true}
                    style={{ width: '100%' }}
                    value={row.HotelAddon?.ChainAddon?.Name}
                /> :
                <div className='extra-addon-dropdown overflow-visible' style={{ marginLeft: '-0px' }}>
                    <HotelAddonDropDown
                        value={value}
                        fluid
                        className='extra-addon-dropdown'
                        keyField="UniqueKey"
                        initial={hotelAddonData.find(x => x.Id === row.HotelAddonId) || 
                            (row.HotelAddonId && row.HotelAddon?.ChainAddon?.Name 
                                ? { Id: row.HotelAddonId, Name: row.HotelAddon.ChainAddon.Name } 
                                : undefined)}
                        data={hotelAddonData.filter(x => moment(x.Date).isSame(row.Date, "date") && x.IsAddonAvailable)}
                        onChange={(item) => {
                            if (value === item) {
                                return;
                            }

                            return applyAddonPrices(item, row);
                        }}
                    /></div>
        );
    }, [hotelAddonData, gridData, reservation])

    const customPropsCallback = useCallback((property) => {
        return (props) => {
            const newProps = { ...props };
            if (!newProps.value) {
                newProps.value = 0;
            }

            if (!newProps.row[property]) {
                newProps.row = { ...newProps.row, [property]: 0 };
            }

            return newProps;
        };
    }, [])

    const isInputDisabled = useCallback((row) => {
        return !!(row.ProductID || row.ProductId || row.FolioTransactionID || row.FolioTransactionId)
    }, [])

    const gridOptions = useMemo(() => ({
        columns: [
            {
                name: 'Date',
                title: text('Date'),
                formatter: createDateFormatter,
                customProps: customPropsCallback('Date')
            },
            {
                name: 'HotelAddonId',
                title: text('AddOn'),
                formatter: createAddonFormatter,
                customProps: customPropsCallback('HotelAddonId'),
            },
            {
                name: 'RoomAmount',
                title: text('RoomAmount'),
                formatter: createEditableFormatter('RoomAmount'),
                customProps: customPropsCallback('RoomAmount')
            },
            {
                name: 'Quantity',
                title: text('Quantity'),
                formatter: createEditableFormatter('Quantity'),
                customProps: customPropsCallback('Quantity')
            },
            {
                name: 'AdultQuantity',
                title: text('Adult'),
                formatter: createEditableFormatter('AdultQuantity'),
                customProps: customPropsCallback('AdultQuantity')
            },
            {
                name: 'ChildQuantity',
                title: text('Child'),
                formatter: createEditableFormatter('ChildQuantity'),
                customProps: customPropsCallback('ChildQuantity')
            },
            {
                name: 'InfantQuantity',
                title: text('Infant'),
                formatter: createEditableFormatter('InfantQuantity'),
                customProps: customPropsCallback('InfantQuantity')
            },
            {
                name: 'AdultAmount',
                title: text('AdultAmount'),
                formatter: createEditableFormatter('AdultAmount'),
                customProps: customPropsCallback('AdultAmount')
            },
            {
                name: 'ChildAmount',
                title: text('ChildAmount'),
                formatter: createEditableFormatter('ChildAmount'),
                customProps: customPropsCallback('ChildAmount')
            },
            {
                name: 'InfantAmount',
                title: text('InfantAmount'),
                formatter: createEditableFormatter('InfantAmount'),
                customProps: customPropsCallback('InfantAmount')
            },
            { name: 'TotalAmount', title: text('TotalAmount'), formatter: totalAmountFormatter },
            { name: 'CurrencyId', title: text('Currency'), formatter: currencyFormatter },
            { name: 'Manual', title: text('Manual'), formatter: manualFormatter },
            { name: 'ProductId', title: text('Product'), formatter: productFormatter },
            { name: 'UpdateDate', title: text('UpdateDate'), formatter: dateFormatter },
            { name: 'DiningRoomId', title: text('DiningRoom') },
            { name: 'DiningRoomStartHour', title: text('StartHour'), formatter: timeFormatter },
            { name: 'DiningRoomEndHour', title: text('EndHour'), formatter: timeFormatter },
        ],
        paging: true,
        selectable: true,
        selectableCallback: row => {
            return !isInputDisabled(row);
        },
        noVirtualTable: true,
        columnExtensions: [
            { columnName: 'Date'},
            { columnName: 'HotelAddonId', width: 250 },
        ]
    }), [gridData, reservation, hotelAddonData])

    return (
        <React.Fragment>
            <Button positive onClick={addNewDailyAddon}>{text('AddNew')}</Button>
            <Button negative onClick={deleteAddon}>{text('Delete')}</Button>
            <div className="extra-addons-grid">
                {isDataLoaded && (
                    <GridPanel
                        data={gridData}
                        options={gridOptions}
                        onSelected={setSelected}
                    />
                )}
            </div>

        </React.Fragment>
    )
}

ExtraAddonsGrid.propTypes = {
    reservation: PropTypes.any,

    hotelId: PropTypes.number,
    startDate: PropTypes.any,
    endDate: PropTypes.any,
    rateId: PropTypes.number,

    setData: PropTypes.func,
    data: PropTypes.array,
    insufficientAddon: PropTypes.bool,
    setInsufficientAddon: PropTypes.func,
    setDataForDeletion: PropTypes.func,
}
export default ExtraAddonsGrid;
